<template>
  <header
    class="sticky top-0 z-50 w-full border-b border-white/10 bg-t3-bg-dark/95 backdrop-blur-md px-4 py-4 sm:px-6 lg:px-20 font-t3-display"
    role="banner"
  >
    <div class="max-w-7xl mx-auto flex items-center justify-between gap-4">
      <div class="flex min-w-0 flex-1 items-center">
        <h2 class="text-lg sm:text-xl font-bold tracking-tight text-slate-100 truncate" :title="name || 'Restaurant'">
          {{ name || 'Restaurant' }}
        </h2>
      </div>
      <nav class="hidden md:flex items-center gap-6 lg:gap-10" aria-label="Main">
        <a class="text-sm font-medium text-slate-200 hover:text-t3-primary transition-colors min-h-[44px] flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary rounded" href="#menu">{{ navLabel || viewListLabel || $t('public.t3NavMenu') }}</a>
        <a v-if="feedbackAvailable" class="text-sm font-medium text-slate-200 hover:text-t3-primary transition-colors min-h-[44px] flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary rounded" href="#reviews">{{ $t('public.t3NavReviews') }}</a>
        <div v-else-if="isOwnerViewer && ownerViewMode" class="py-2 px-3 rounded border border-dashed border-white/30 bg-white/10 max-w-[140px]">
          <p class="text-xs font-mono text-slate-300 break-words whitespace-normal">{{ $t('public.ownerNavReviewsUnavailable') }}</p>
        </div>
        <PublicReservationTrigger
          v-if="reservationAvailable"
          :slug="slug"
          template="template-3"
          :primary-color="primaryColor"
        >
          <template #button="{ openModal }">
            <button
              type="button"
              class="text-t3-bg-dark px-6 py-2 rounded-lg font-bold text-sm transition-all min-h-[44px] flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary focus-visible:ring-offset-2"
              :class="primaryColor ? 'hover:opacity-90' : 'bg-t3-primary hover:bg-t3-primary/90'"
              :style="primaryColor ? { backgroundColor: primaryColor } : undefined"
              :aria-label="reserveButtonLabel || $t('reservation.reserveButton')"
              data-testid="reservation-open-modal"
              @click="openModal"
            >
              {{ reserveButtonLabel || $t('reservation.reserveButton') }}
            </button>
          </template>
        </PublicReservationTrigger>
        <div
          v-else-if="isOwnerViewer && ownerViewMode"
          class="py-2 px-3 rounded border border-dashed border-white/30 bg-white/10 max-w-[140px]"
        >
          <p class="text-xs font-mono text-slate-300 break-words whitespace-normal">{{ $t('public.ownerNavBookingUnavailable') }}</p>
        </div>
        <LanguageDropdown
          :languages="languages"
          :current-locale="currentLocale"
          class="rms-language-dropdown--t3 text-slate-200 border-white/20"
          @select-locale="$emit('select-locale', $event)"
        />
      </nav>
      <div class="flex shrink-0 items-center gap-2 md:hidden">
        <LanguageDropdown
          :languages="languages"
          :current-locale="currentLocale"
          class="rms-language-dropdown--t3 text-slate-200 border-white/20"
          @select-locale="$emit('select-locale', $event)"
        />
        <button
          type="button"
          class="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-200 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary"
          :aria-label="mobileNavOpen ? $t('app.close') : (viewListAriaLabel || $t('public.t3NavMenu'))"
          :aria-expanded="mobileNavOpen"
          data-testid="header-mobile-menu-btn"
          @click="mobileNavOpen = !mobileNavOpen"
        >
          <span class="material-symbols-outlined text-3xl">{{ mobileNavOpen ? 'close' : 'menu' }}</span>
        </button>
      </div>
    </div>
    <!-- Mobile nav panel -->
    <Transition name="rms-mobile-nav">
      <div
        v-if="mobileNavOpen"
        class="fixed inset-0 top-20 z-40 md:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
      >
        <div class="absolute inset-0 bg-black/50" aria-hidden="true" @click="mobileNavOpen = false" />
        <nav class="absolute left-0 right-0 top-0 bg-t3-bg-dark border-b border-white/10 shadow-lg flex flex-col py-4 px-4 gap-1" aria-label="Main">
          <a class="text-base font-medium min-h-[44px] flex items-center px-4 rounded-lg hover:bg-white/5 text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary" href="#menu" @click="mobileNavOpen = false">{{ navLabel || viewListLabel || $t('public.t3NavMenu') }}</a>
          <a v-if="feedbackAvailable" class="text-base font-medium min-h-[44px] flex items-center px-4 rounded-lg hover:bg-white/5 text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary" href="#reviews" @click="mobileNavOpen = false">{{ $t('public.t3NavReviews') }}</a>
          <div v-else-if="isOwnerViewer && ownerViewMode" class="py-3 px-3 rounded border border-dashed border-white/30 bg-white/10" @click="mobileNavOpen = false">
            <p class="text-xs font-mono text-slate-300">{{ $t('public.ownerNavReviewsUnavailable') }}</p>
          </div>
          <PublicReservationTrigger
            v-if="reservationAvailable"
            :slug="slug"
            template="template-3"
            :primary-color="primaryColor"
            :button-label="reserveButtonLabel"
            :modal-title="reserveModalHeading"
            :pax-label="reservePaxLabel"
          >
<template #button="{ openModal }">
            <button
              type="button"
              class="w-full text-left text-base font-bold min-h-[44px] flex items-center px-4 rounded-lg text-t3-bg-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary"
              :class="primaryColor ? 'hover:opacity-90' : 'bg-t3-primary hover:bg-t3-primary/90'"
              :style="primaryColor ? { backgroundColor: primaryColor } : undefined"
              :aria-label="reserveButtonLabel || $t('reservation.reserveButton')"
              data-testid="reservation-open-modal-mobile"
              @click="openModal()"
            >
              {{ reserveButtonLabel || $t('reservation.reserveButton') }}
            </button>
          </template>
          </PublicReservationTrigger>
          <div
            v-else-if="isOwnerViewer && ownerViewMode"
            class="py-4 px-4 rounded border border-dashed border-white/30 bg-white/10"
          >
            <p class="text-xs font-mono text-slate-300">{{ $t('public.ownerNavBookingUnavailable') }}</p>
          </div>
        </nav>
      </div>
    </Transition>
    <div
      v-if="showOwnerNeedsDataNotice"
      class="max-w-7xl mx-auto px-4 sm:px-6 py-3"
      role="status"
      aria-live="polite"
    >
      <div class="py-4 px-4 rounded border border-dashed border-white/30 bg-white/10">
        <p class="text-sm font-mono text-slate-300">{{ $t('public.ownerNeedsDataIntro') }}</p>
        <a
          v-if="ownerAdminUrl"
          :href="ownerAdminUrl"
          class="mt-2 inline-flex items-center text-sm font-mono text-t3-primary underline underline-offset-2 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-t3-primary focus-visible:ring-offset-1 rounded min-h-[44px]"
          :aria-label="$t('public.openAdminPage')"
        >
          {{ $t('public.openAdminPage') }}
        </a>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed } from 'vue'
import LanguageDropdown from '@/components/public/LanguageDropdown.vue'
import PublicReservationTrigger from '@/components/public/PublicReservationTrigger.vue'

const mobileNavOpen = ref(false)

const props = defineProps({
  name: { type: String, default: '' },
  viewer: { type: Object, default: () => ({ is_owner: false, owner_admin_url: null, needs_data: false }) },
  ownerViewMode: { type: Boolean, default: true },
  languages: { type: Array, default: () => [] },
  currentLocale: { type: String, default: '' },
  reservationAvailable: { type: Boolean, default: false },
  slug: { type: String, default: '' },
  primaryColor: { type: String, default: null },
  viewListLabel: { type: String, default: '' },
  navLabel: { type: String, default: '' },
  viewListAriaLabel: { type: String, default: '' },
  reserveButtonLabel: { type: String, default: '' },
  reserveModalHeading: { type: String, default: '' },
  reservePaxLabel: { type: String, default: '' },
  feedbackAvailable: { type: Boolean, default: false },
})

const isOwnerViewer = computed(() => props.viewer?.is_owner === true)
const ownerAdminUrl = computed(() => {
  const url = props.viewer?.owner_admin_url
  return typeof url === 'string' && url.trim() !== '' ? url : null
})
const showOwnerNeedsDataNotice = computed(() =>
  props.ownerViewMode && isOwnerViewer.value && props.viewer?.needs_data === true
)

defineEmits(['select-locale'])
</script>

<style scoped>
.rms-mobile-nav-enter-active,
.rms-mobile-nav-leave-active {
  transition: opacity 0.2s ease;
}
.rms-mobile-nav-enter-active nav,
.rms-mobile-nav-leave-active nav {
  transition: transform 0.2s ease;
}
.rms-mobile-nav-enter-from,
.rms-mobile-nav-leave-to {
  opacity: 0;
}
.rms-mobile-nav-enter-from nav,
.rms-mobile-nav-leave-to nav {
  transform: translateY(-8px);
}
</style>
